class IDBCache {
    constructor(dbName = "RequestCache", version = 1) {
        this.dbName = dbName;
        this.version = version;
        this.db = null;
        this.storeName = "requests";
    }

    // Inicializa o banco de dados
    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                
                // Cria o object store se não existir
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, { keyPath: "key" });
                    
                    // Cria índices para busca eficiente
                    store.createIndex("url", "url", { unique: false });
                    store.createIndex("timestamp", "timestamp", { unique: false });
                    store.createIndex("expires", "expires", { unique: false });
                }
            };
        });
    }

    // Gera uma chave única para a requisição
    generateKey(url, method = "GET", body = null) {
        const keyData = {
            url,
            method,
            body: body ? JSON.stringify(body) : null
        };
        return btoa(JSON.stringify(keyData)); // Codifica em base64
    }

    // Salva uma requisição no cache
    async set(url, response, options = {}) {
        if (!this.db) await this.init();
        
        const {
            method = "GET",
            body = null,
            ttl = 3600000, // 1 hora padrão
            customKey = null
        } = options;

        const key = customKey || this.generateKey(url, method, body);
        const timestamp = Date.now();
        const expires = timestamp + ttl;

        const cacheItem = {
            key,
            url,
            method,
            body,
            response,
            timestamp,
            expires,
            headers: options.headers || {}
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readwrite");
            const store = transaction.objectStore(this.storeName);
            
            const request = store.put(cacheItem);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(key);
        });
    }

    // Recupera uma requisição do cache
    async get(url, method = "GET", body = null, customKey = null) {
        if (!this.db) await this.init();
        
        const key = customKey || this.generateKey(url, method, body);

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readonly");
            const store = transaction.objectStore(this.storeName);
            
            const request = store.get(key);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const result = request.result;
                
                // Verifica se o item existe e não expirou
                if (result && result.expires > Date.now()) {
                    resolve(result);
                } else {
                    // Remove item expirado
                    if (result) this.delete(key);
                    resolve(null);
                }
            };
        });
    }

    // Remove um item do cache
    async delete(url, method = "GET", body = null, customKey = null) {
        if (!this.db) await this.init();
        
        const key = customKey || this.generateKey(url, method, body);

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readwrite");
            const store = transaction.objectStore(this.storeName);
            
            const request = store.delete(key);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(true);
        });
    }
     async deleteByUrlAndMethod(url, method = "GET") {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readwrite");
            const store = transaction.objectStore(this.storeName);
            const index = store.index("url");
            
            // Usar um cursor para iterar sobre os itens com esta URL
            const range = IDBKeyRange.only(url);
            const request = index.openCursor(range);

            let deletedCount = 0;

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const cursor = request.result;
                
                if (cursor) {
                    const item = cursor.value;
                    
                    // Verificar se o método corresponde
                    if (item.method === method) {
                        cursor.delete();
                        deletedCount++;
                    }
                    
                    cursor.continue();
                } else {
                    // Cursor terminou
                    resolve(deletedCount);
                }
            };
        });
    }

    // Limpa todo o cache ou itens expirados
    async clear(onlyExpired = false) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readwrite");
            const store = transaction.objectStore(this.storeName);
            const index = store.index("expires");
            const now = Date.now();

            if (onlyExpired) {
                // Remove apenas itens expirados
                const range = IDBKeyRange.upperBound(now);
                const request = index.openCursor(range);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve(true);
                    }
                };
            } else {
                // Remove tudo
                const request = store.clear();
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve(true);
            }
        });
    }

    async clearAllForLogout() {
        if (!this.db) await this.init();
        
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readwrite");
            const store = transaction.objectStore(this.storeName);
            
            const request = store.clear();
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                console.log("✅ Cache completamente limpo para logout");
                resolve(true);
            };
        });
    }

    // MÉTODO ADICIONADO: Destrói completamente o banco de dados (limpeza mais radical)
    async destroyDatabase() {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
        
        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(this.dbName);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                console.log("🗑️ Banco de dados de cache completamente destruído");
                resolve(true);
            };
            
            request.onblocked = () => {
                console.warn("⚠️ A destruição do banco está bloqueada - feche outras conexões");
                reject(new Error("Database deletion blocked"));
            };
        });
    }

    // Retorna estatísticas do cache
    async getStats() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readonly");
            const store = transaction.objectStore(this.storeName);
            const request = store.getAll();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const items = request.result;
                const now = Date.now();
                
                const stats = {
                    total: items.length,
                    expired: items.filter(item => item.expires <= now).length,
                    valid: items.filter(item => item.expires > now).length,
                    oldest: items.length > 0 ? Math.min(...items.map(item => item.timestamp)) : null,
                    newest: items.length > 0 ? Math.max(...items.map(item => item.timestamp)) : null
                };

                resolve(stats);
            };
        });
    }

    // Busca itens por URL (útil para limpar cache de endpoints específicos)
    async getByUrl(urlPattern, method = null, body = null) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], "readonly");
            const store = transaction.objectStore(this.storeName);
            const index = store.index("url");
            const request = index.getAll();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const items = request.result;
                if (body && method) {
                    const filtered_ = items.filter(item => 
                        (item.method.includes(method) && item.url.includes(urlPattern) && item.body == body)
                    );
                    resolve(_.first(filtered_))
                } else {
                    const filtered = items.filter(item => 
                        (!method && item.url.includes(urlPattern)) ||
                        (!!method && item.method.includes(method) && item.url.includes(urlPattern))
                    );
                    resolve(filtered);
                }
                
            };
        });
    }

    // Remove todos os itens de uma URL específica
    async deleteByUrl(urlPattern) {
        const items = await this.getByUrl(urlPattern);
        
        for (const item of items) {
            await this.delete(item.url, item.method, item.body, item.key);
        }
        
        return items.length;
    }
}

// Exemplo de uso:
const cache = new IDBCache();
window._cache = cache;
export default cache;


// Como usar na prática:
/*
const cache = new IDBCache();

// Salvar uma requisição no cache
await cache.set("/api/users", { data: [...] }, {
    ttl: 300000, // 5 minutos
    method: "GET"
});

// Recuperar do cache
const cached = await cache.get("/api/users", "GET");
if (cached) {
    console.log("Dados do cache:", cached.response);
} else {
    // Fazer requisição real
    const response = await fetch("/api/users");
    const data = await response.json();
    
    // Salvar no cache
    await cache.set("/api/users", data, {
        ttl: 300000,
        headers: response.headers
    });
}

// Limpar itens expirados
await cache.clear(true);

// Ver estatísticas
const stats = await cache.getStats();
console.log("Estatísticas do cache:", stats);
*/